import { jwtDecode } from "jwt-decode";
import { baseURL } from "./baseUrl";
import { clearPersistedTenantId } from "../utils/tenantStorage";
import { triggerAuthFailure } from "../utils/authFailureHandler";

// Small fetch wrapper for the auth endpoints. Throws on non-2xx so callers
// keep their try/catch shape; the thrown error carries `status` for logging.
// `credentials: 'include'` is the fetch equivalent of axios's withCredentials
// (the refresh cookie lives on the fastapi origin).
const postJson = async (url, body = null, {token = null, withCredentials = false} = {}) => {
    const headers = {};
    if (body !== null) headers['Content-Type'] = 'application/json';
    if (token) headers['Authorization'] = 'Bearer ' + token;

    const response = await fetch(url, {
        method: 'POST',
        headers,
        body: body !== null ? JSON.stringify(body) : undefined,
        credentials: withCredentials ? 'include' : 'same-origin',
    });

    const isJSON = response.headers.get('Content-Type')?.includes('application/json');
    const data = isJSON ? await response.json() : await response.text();

    if (!response.ok) {
        const err = new Error(`Request failed with status code ${response.status}`);
        err.status = response.status;
        throw err;
    }
    return {data, status: response.status};
};

// Role claim of the token rpc/login hands to superadmins before the
// authenticator step (mirrors public.login() / src/core/totp.py). It is not
// a usable session: only /auth/totp/* accept it.
export const TOTP_PENDING_ROLE = 'totp_pending';

export default class Auth {
    name = null;
    isLoggedIn = false;
    userEmail = null;
    userPassword = null;
    jwtToken = null;
    jwtTokenExpiry = null;
    // Superadmin login in progress: the pending token until the TOTP code
    // is verified, and whether an authenticator is already enrolled.
    pendingToken = null;
    pendingTotpEnrolled = false;
    _refreshInFlight = null;
    _renewLoopRunning = false;

    setCredentials(newEmail, newPassword) {
        this.userEmail = newEmail;
        this.userPassword = newPassword;
    }

    // Renew the access token via the refresh cookie shortly before it expires.
    // Replaces the old password-replay loop; no credentials are kept in memory.
    async _keepLoggedIn() {
        // One renewal loop per Auth instance; login() and restoreSession() can
        // both start it (StrictMode runs App's restore effect twice in dev).
        if (this._renewLoopRunning) return;
        this._renewLoopRunning = true;
        try {
            while (this.isLoggedIn) {
                let waitTime = this.jwtTokenExpiry * 1000 - Date.now() - 30000;
                if (waitTime < 0) waitTime = 0;
                await new Promise(resolve => setTimeout(resolve, waitTime));

                if (!this.isLoggedIn) break;

                const renewed = await this.refreshSession();
                if (!renewed) {
                    triggerAuthFailure();
                    break;
                }
            }
        } finally {
            this._renewLoopRunning = false;
        }
    }

    // Resolves {message: 'Success'} once signed in, or
    // {message: 'TOTP_REQUIRED', enrolled} when the account is a superadmin
    // and the authenticator code is still needed (see totpSetup/totpVerify).
    async login(newEmail, newPassword) {
        this._clearPending();
        this.setCredentials(newEmail, newPassword)
        let resp = await this._loginPostRequest()
        // The password is only needed for the login request itself.
        this.userPassword = null;
        if (this.pendingToken) {
            return {'message': 'TOTP_REQUIRED', 'enrolled': this.pendingTotpEnrolled}
        }
        if (this.isLoggedIn) {
            await this._establishSession();
            this._keepLoggedIn();
            return {'message': 'Success'}
        } else {
            return resp
        }
    }

    // First superadmin login: create the authenticator secret. Resolves
    // {otpauth_uri, qr_svg, manual_key} or throws (status on the error).
    async totpSetup() {
        if (!this.pendingToken) throw new Error('No login in progress');
        const resp = await postJson(baseURL('fastapi', 'auth/totp/setup'), null, {
            token: this.pendingToken,
        });
        return resp.data;
    }

    // Verify the authenticator code; on success the account is signed in
    // exactly as login() would have done (access token + refresh cookie).
    async totpVerify(code) {
        if (!this.pendingToken) return {'message': 'No login in progress'};
        try {
            const resp = await postJson(baseURL('fastapi', 'auth/totp/verify'), {code}, {
                token: this.pendingToken,
                withCredentials: true,
            });
            this._clearPending();
            this._setToken(true, resp.data.token);
            this._keepLoggedIn();
            return {'message': 'Success'};
        } catch (e) {
            console.warn('TOTP verification failed:', e.status ?? 'no response', e.message);
            if (e.status === 401 && this.pendingToken && this._pendingExpired()) {
                // The pending token outlived its 5 minutes: start over.
                this._clearPending();
                return {'message': 'Login expired, please sign in again', 'restart': true};
            }
            if (e.status === 401) return {'message': 'Invalid authenticator code'};
            if (e.status === 429) return {'message': 'Too many wrong codes, try again in 15 minutes', 'restart': true};
            if (e.status === 400) return {'message': 'Authenticator enrolment required'};
            return {'message': 'Could not verify the code, please try again'};
        }
    }

    cancelTotp() {
        this._clearPending();
    }

    _clearPending() {
        this.pendingToken = null;
        this.pendingTotpEnrolled = false;
    }

    _pendingExpired() {
        try {
            const {exp} = jwtDecode(this.pendingToken);
            return exp * 1000 <= Date.now();
        } catch (e) {
            return true;
        }
    }

    logout() {
        // Revoke the refresh session server-side (fire-and-forget)
        postJson(baseURL('fastapi', 'auth/logout'), null, {withCredentials: true})
            .catch(() => {});
        // Get rid of the user state
        this.userEmail = null;
        this.userPassword = null;
        this.name = null;
        this.isLoggedIn = false;
        this.jwtToken = null;
        this.jwtTokenExpiry = null;
        this._clearPending();
        // Clear persisted tenant selection
        clearPersistedTenantId();
    }

    isAuthenticated() {
        // Return true if user is authenticated and the JWT is still valid.
        // jwtTokenExpiry is in seconds (JWT `exp` claim), Date.now() is in ms.
        return Boolean(
            this.isLoggedIn &&
            this.jwtToken &&
            this.jwtTokenExpiry * 1000 > Date.now()
        );
    }

    async _loginPostRequest() {
        if (this.userEmail && this.userPassword) {
            const url = baseURL('api', 'rpc/login')

            // Makes the HTTP request to obtain JWT token + jwtTokenExpiry + user details
            try {
                let resp = await postJson(url, {
                    email: this.userEmail,
                    password: this.userPassword
                });
                let data = resp.data;
                const decoded = jwtDecode(data.token);
                if (decoded.role === TOTP_PENDING_ROLE) {
                    // Superadmin: password accepted, authenticator code next.
                    this.pendingToken = data.token;
                    this.pendingTotpEnrolled = Boolean(decoded.totp_enrolled);
                    this.isLoggedIn = false;
                    return resp;
                }
                this._setToken(true, data.token);
                return resp;
            } catch (e) {
                this.isLoggedIn = false;
                console.error('ERROR:', e);
                return e
            }
        }
    }

    // Exchange the fresh access token for an HttpOnly refresh cookie.
    async _establishSession() {
        try {
            await postJson(baseURL('fastapi', 'auth/session'), null, {
                token: this.jwtToken,
                withCredentials: true,
            });
        } catch (e) {
            // Session continues on the current token; renewal will fail later
            // and trigger the normal auth-failure logout.
            console.error('Failed to establish refresh session:', e);
        }
    }

    // Get a new access token using the refresh cookie. Returns true on success.
    //
    // Refresh tokens are single-use: the server rotates the cookie on every
    // call and treats a replay of the previous token as theft, revoking the
    // whole session chain. Concurrent callers must therefore share one
    // request, otherwise the second one logs the user out.
    refreshSession() {
        if (!this._refreshInFlight) {
            this._refreshInFlight = this._refreshSessionOnce().finally(() => {
                this._refreshInFlight = null;
            });
        }
        return this._refreshInFlight;
    }

    async _refreshSessionOnce() {
        try {
            const resp = await postJson(baseURL('fastapi', 'auth/refresh'), null, {
                withCredentials: true,
            });
            this._setToken(true, resp.data.token);
            return true;
        } catch (e) {
            // Log status and message only — never the full error object, which
            // can embed the response body.
            console.warn('Session refresh failed:',
                e.status ?? 'no response', e.message);
            this.isLoggedIn = false;
            return false;
        }
    }

    // Restore a session after a page refresh using the refresh cookie.
    // Returns true if the user is logged back in.
    async restoreSession() {
        const restored = await this.refreshSession();
        if (restored) this._keepLoggedIn();
        return restored;
    }

    // Used to set the relevant parts of this class
    _setToken(status, token) {
        const decoded = jwtDecode(token);

        this.isLoggedIn = status;
        this.jwtToken = token;
        if (decoded.email) this.userEmail = decoded.email;
        // Keep expiry in seconds (JWT `exp` claim unit); fallback: one hour from now.
        this.jwtTokenExpiry = decoded.exp >= 0 ? decoded.exp : Math.floor(Date.now() / 1000) + 60 * 60;
    }

    toString() {
        // Never include credentials or tokens here: this output can end up in logs.
        return JSON.stringify({
            name: this.name,
            isLoggedIn: this.isLoggedIn,
            userEmail: this.userEmail,
            jwtTokenExpiry: this.jwtTokenExpiry
        });
    }
}
